import { useQuery, useMutation, UseQueryResult } from '@tanstack/react-query';
import { queryKeysClient } from 'common/clients/query-key.client';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import {
  addPermissionToBranchRoleService,
  counterpartyCreateBranchService,
  createRoleInBranchRoleService,
  deleteRoleInBranchRoleService,
  removePermissionToBranchRoleService,
} from '../services/branch.service';
import {
  TAddBranchRolePermissionReqData,
  TCounterpartyAvailableBranchesResData,
  TCounterpartyBranchesReqParams,
  TCounterpartyBranchesResData,
  TCounterpartyCreateBranchReqData,
  TCreateRoleInBranchRoleReqData,
  TDeleteBranchRolePermissionReqData,
  TDeleteRoleInBranchRoleReqData,
  TPermissionTableByBranchReqParams,
  TPermissionTableByBranchResData,
} from '../types/branch.types';

export const useGetBranchesListQuery = (
  counterpartyGUID: string,
  params: TCounterpartyBranchesReqParams,
  options?: any,
): UseQueryResult<TCounterpartyBranchesResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.BranchQueryKeys].getBranches(counterpartyGUID, params),
    ...options,
  });

export const useGetAvailableBranchesListQuery = (
  counterpartyGUID: string,
  params?: TCounterpartyBranchesReqParams,
  options?: any,
): UseQueryResult<TCounterpartyAvailableBranchesResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.BranchQueryKeys].getAvailableBranches(counterpartyGUID, params),
    ...options,
  });

export const useGetBranchesMultiRoleQuery_MultiRole = (
  counterpartyGUID: string,
  params?: TCounterpartyBranchesReqParams,
  hasAccessForAllBranches?: boolean,
  options?: any,
): UseQueryResult<TCounterpartyBranchesResData | TCounterpartyAvailableBranchesResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.BranchQueryKeys]
      .getBranches_MultiRole(counterpartyGUID, params, hasAccessForAllBranches),
    ...options,
  });

export const useGetPermissionTableByBranchQuery = (
  counterpartyGUID: string,
  branchGUID: string,
  params: TPermissionTableByBranchReqParams,
  options?: any,
): UseQueryResult<TPermissionTableByBranchResData> =>
  useQuery({
    ...queryKeysClient[EQueryKeys.BranchQueryKeys].getPermissionTableByBranch(counterpartyGUID, branchGUID, params),
    ...options,
  });

export const useCreateBranchMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string; requestData: TCounterpartyCreateBranchReqData }) =>
      counterpartyCreateBranchService(data.counterpartyGUID, data.requestData),
  });

export const useAddPermissionToBranchRoleMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string; requestData: TAddBranchRolePermissionReqData }) =>
      addPermissionToBranchRoleService(data.counterpartyGUID, data.requestData),
  });

export const useRemovePermissionToBranchRoleMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string; requestData: TDeleteBranchRolePermissionReqData }) =>
      removePermissionToBranchRoleService(data.counterpartyGUID, data.requestData),
  });

export const useDeleteRoleInBranchRoleMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string; requestData: TDeleteRoleInBranchRoleReqData }) =>
      deleteRoleInBranchRoleService(data.counterpartyGUID, data.requestData),
  });

export const useCreateRoleInBranchRoleMutation = (): any =>
  useMutation({
    mutationFn: (data: { counterpartyGUID: string; requestData: TCreateRoleInBranchRoleReqData }) =>
      createRoleInBranchRoleService(data.counterpartyGUID, data.requestData),
  });
